// The 3D scene behind the menus: a themed floating island, Sai + pet, and a voxel logo.
import * as THREE from 'three';
import { VoxelWorld, blockGeometry } from './voxel.js';
import { B } from './blocks.js';
import { THEMES } from './themes.js';
import { generateSmallIsland } from './levelgen.js';
import { Pet } from './pet.js';
import { easeOutBack, damp } from './util.js';

const FONT = {
  S: ['.####', '#....', '#....', '.###.', '....#', '....#', '####.'],
  A: ['.###.', '#...#', '#...#', '#####', '#...#', '#...#', '#...#'],
  I: ['###', '.#.', '.#.', '.#.', '.#.', '.#.', '###'],
  C: ['.####', '#....', '#....', '#....', '#....', '#....', '.####'],
  R: ['####.', '#...#', '#...#', '####.', '#.#..', '#..#.', '#...#'],
  F: ['#####', '#....', '#....', '####.', '#....', '#....', '#....'],
  T: ['#####', '..#..', '..#..', '..#..', '..#..', '..#..', '..#..'],
};

class Logo {
  constructor(group, mats) {
    const word = 'SAICRAFT';
    const blocks = [];
    let x = 0;
    for (let li = 0; li < word.length; li++) {
      const ch = FONT[word[li]];
      const w = ch[0].length;
      for (let r = 0; r < 7; r++)
        for (let c = 0; c < w; c++)
          if (ch[r][c] === '#') blocks.push({ x: x + c, y: 6 - r, li, sai: li < 3 });
      x += w + 1;
    }
    const total = x - 1;
    this.blocks = blocks;
    const S = 0.5;
    this.S = S;
    this.inst = {};
    const kinds = [
      ['sai', B.GOLD_BLOCK, blocks.filter((b) => b.sai)],
      ['craft', B.DIAMOND_BLOCK, blocks.filter((b) => !b.sai)],
    ];
    this.group = new THREE.Group();
    for (const [k, id, list] of kinds) {
      const mat = mats.solid.clone();
      mat.emissive = new THREE.Color(k === 'sai' ? '#ffb020' : '#30e0ff');
      mat.emissiveMap = null;
      mat.emissiveIntensity = 0.32;
      const m = new THREE.InstancedMesh(blockGeometry(id), mat, list.length);
      m.castShadow = true;
      m.frustumCulled = false;
      list.forEach((b) => {
        b.tx = (b.x - (total - 1) / 2) * S;
        b.ty = (b.y - 3) * S;
        b.sx = (Math.random() - 0.5) * 40;
        b.sy = -20 - Math.random() * 20;
        b.sz = (Math.random() - 0.5) * 30;
        b.delay = b.li * 0.08 + Math.random() * 0.5;
      });
      this.inst[k] = { m, list };
      this.group.add(m);
    }
    // shadow/backing slab so the logo pops against the sky
    group.add(this.group);
    this.t = 0;
    this.m4 = new THREE.Matrix4();
    this.q = new THREE.Quaternion();
    this.e = new THREE.Euler();
    this.v = new THREE.Vector3();
    this.s = new THREE.Vector3();
  }
  replay() {
    this.t = 0;
  }
  update(dt) {
    this.t += dt;
    for (const k of Object.keys(this.inst)) {
      const { m, list } = this.inst[k];
      list.forEach((b, i) => {
        const k2 = Math.max(0, Math.min(1, (this.t - b.delay) / 0.9));
        const e = easeOutBack(k2);
        const wave = Math.sin(this.t * 2.2 - b.li * 0.6) * 0.12 * Math.min(1, Math.max(0, this.t - 2.5));
        this.v.set(b.sx + (b.tx - b.sx) * e, b.sy + (b.ty - b.sy) * e + wave, b.sz * (1 - e));
        this.e.set((1 - k2) * 5, (1 - k2) * 7, 0);
        this.q.setFromEuler(this.e);
        const sc = this.S * (0.3 + 0.7 * Math.min(1, k2 * 1.4));
        this.s.set(sc, sc, sc);
        this.m4.compose(this.v, this.q, this.s);
        m.setMatrixAt(i, this.m4);
      });
      m.instanceMatrix.needsUpdate = true;
    }
  }
}

export class MenuScene {
  constructor(game) {
    this.game = game;
    this.group = new THREE.Group();
    game.scene.add(this.group);
    this.world = null;
    this.mode = 'title';
    this.t = 0;
    this.themeKey = null;
    this.camPos = new THREE.Vector3(0, 47, -18);
    this.camLook = new THREE.Vector3(0, 46, 0);
    this.spin = 0;
    this.dragYaw = 0;
  }

  setTheme(key) {
    this.game.applyTheme(key); // a level may have changed the sky since we were last here
    if (key === this.themeKey) return;
    this.themeKey = key;
    const theme = THEMES[key];
    if (this.world) {
      this.group.remove(this.world.group);
      this.world.dispose();
    }
    this.world = new VoxelWorld(this.game.mats);
    const { g } = generateSmallIsland(this.world, theme, 31, 9);
    // a little themed podium for Sai
    for (let x = -1; x <= 1; x++) for (let z = -1; z <= 1; z++) this.world.set(x, 40, z, x === 0 && z === 0 ? B.GOLD_BLOCK : theme.stone);
    for (let x = -1; x <= 1; x++) for (let z = -1; z <= 1; z++) for (let y = 41; y <= 45; y++) this.world.set(x, y, z, 0);
    // keep the view in front of Sai clear (trees go behind/beside him)
    for (let x = -6; x <= 6; x++) for (let z = -11; z <= 1; z++) for (let y = 41; y <= 52; y++) if (this.world.get(x, y, z) && !(Math.abs(x) <= 1 && Math.abs(z) <= 1)) this.world.set(x, y, z, 0);
    // a couple of decorative pieces
    this.world.set(-5, 41, 2, B.LUCKY);
    for (let y = 41; y <= 43; y++) this.world.set(5, y, 3, theme.stone);
    this.world.set(5, 44, 3, B.GLOWSTONE);
    void g;
    this.world.rebuild();
    this.group.add(this.world.group);
  }

  setCharacter(ch, petKind) {
    if (this.ch) {
      this.group.remove(this.ch.root);
      this.ch.dispose();
    }
    this.ch = ch;
    ch.root.position.set(0.5, 41, 0.5);
    ch.root.rotation.y = Math.PI;
    if (ch.tag) ch.tag.visible = false;
    this.group.add(ch.root);
    if (this.pet) {
      this.pet.dispose();
      this.pet = null;
    }
    if (petKind && petKind !== 'none') {
      this.pet = new Pet({ group: this.group, player: null, fx: this.game.fx }, petKind);
      this.pet.obj.position.set(-1, 42.6, -0.6);
    }
  }

  showLogo(on) {
    if (on && !this.logo) {
      this.logo = new Logo(this.group, this.game.mats);
      this.logo.group.position.set(0.5, 43.55, -0.6);
      this.logo.group.rotation.y = Math.PI;
    }
    if (this.logo) {
      this.logo.group.visible = on;
      if (on) this.logo.replay();
    }
  }

  setMode(mode) {
    this.mode = mode;
    this.showLogo(mode === 'title');
  }

  update(dt, input) {
    this.t += dt;
    const t = this.t;
    if (this.logo && this.logo.group.visible) {
      this.logo.update(dt);
      // fit the logo to ~78% of the visible width
      const cam = this.game.camera;
      const d = Math.max(1, this.logo.group.position.z - cam.position.z);
      const visW = 2 * d * Math.tan(THREE.MathUtils.degToRad(cam.fov / 2)) * cam.aspect;
      const logoW = 47 * this.logo.S;
      this.logo.group.scale.setScalar(Math.min(1.15, (visW * 0.78) / logoW));
    }
    if (this.ch) {
      let celebrate = false, wave = false;
      if (this.mode === 'title') wave = Math.sin(t * 0.7) > 0.6;
      if (this.mode === 'locker') {
        this.dragYaw += input.camDX * 0.01;
        if (Math.abs(input.camDX) < 0.01) this.dragYaw += dt * 0.5;
        this.ch.root.rotation.y = Math.PI + this.dragYaw;
      } else this.ch.root.rotation.y = damp(this.ch.root.rotation.y, Math.PI, 4, dt);
      if (this.mode === 'worlds') celebrate = false;
      this.ch.update(dt, { speed: 0, grounded: true, wave, celebrate });
      if (this.pet) this.pet.follow(dt, this.ch.root.position, this.ch.root.rotation.y);
    }
    const { pos, look } = this.camTarget();
    this.camPos.x = damp(this.camPos.x, pos.x, 2.5, dt);
    this.camPos.y = damp(this.camPos.y, pos.y, 2.5, dt);
    this.camPos.z = damp(this.camPos.z, pos.z, 2.5, dt);
    this.camLook.x = damp(this.camLook.x, look.x, 3, dt);
    this.camLook.y = damp(this.camLook.y, look.y, 3, dt);
    this.camLook.z = damp(this.camLook.z, look.z, 3, dt);
    const cam = this.game.camera;
    cam.position.copy(this.camPos);
    cam.lookAt(this.camLook);
  }

  camTarget() {
    const t = this.t;
    const cam = this.game.camera;
    let pos, look;
    const aspect = cam.aspect;
    const narrow = aspect < 1.2;
    if (this.mode === 'title') {
      const sway = Math.sin(t * 0.18) * 0.5;
      pos = new THREE.Vector3(0.5 + sway, 42.3, -5.6 - (narrow ? 4 : 0));
      look = new THREE.Vector3(0.5, 41.25, 4);
    } else if (this.mode === 'worlds') {
      const a = t * 0.12;
      const R = narrow ? 30 : 23;
      pos = new THREE.Vector3(Math.sin(a) * R, 52, Math.cos(a) * R);
      look = new THREE.Vector3(0, 41, 0);
    } else if (this.mode === 'locker') {
      pos = new THREE.Vector3(narrow ? 0.5 : -1.6, 43, -5.6 - (narrow ? 2 : 0));
      look = new THREE.Vector3(narrow ? 0.5 : -1.6, 42.1, 0.5);
    } else {
      pos = new THREE.Vector3(0, 50, -20);
      look = new THREE.Vector3(0, 42, 0);
    }
    return { pos, look };
  }

  snapCamera() {
    const { pos, look } = this.camTarget();
    this.camPos.copy(pos);
    this.camLook.copy(look);
    this.game.camera.position.copy(pos);
    this.game.camera.lookAt(look);
  }

  dispose() {
    if (this.world) this.world.dispose();
    if (this.ch) this.ch.dispose();
    if (this.pet) this.pet.dispose();
    this.game.scene.remove(this.group);
  }
}
